import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, CreditCard, Mail, HandCoins, DollarSign, Loader2, AlertCircle, Zap } from "lucide-react";
import { API } from "@/lib/api";
import { money } from "./utils";

const REASON_COPY = {
  no_hold_placed: {
    label: "No hold placed",
    tone: "bg-red-100 text-red-800 border-red-300",
    hint: "The customer never completed the Stripe checkout — no card on file yet. Send them a new payment link or collect offline.",
  },
  collect_balance: {
    label: "Balance owed",
    tone: "bg-amber-100 text-amber-800 border-amber-300",
    hint: "Final fare exceeds what was captured from the hold. Charge the saved card, send a payment link, or record an offline payment.",
  },
  card_declined: {
    label: "Card declined",
    tone: "bg-red-100 text-red-800 border-red-300",
    hint: "The last charge attempt was declined. Retry, send a fresh link so the customer can update their card, or collect offline.",
  },
  manual_review: {
    label: "Manual review",
    tone: "bg-slate-100 text-slate-800 border-slate-300",
    hint: "Marked completed without a captured amount — please review and pick the right action.",
  },
};

async function apiPost(auth, path, body) {
  const res = await fetch(`${API}/nemt/admin/rides/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Dispatcher-Auth": auth },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Server ${res.status}`);
  return data;
}

async function apiGet(auth, path) {
  const res = await fetch(`${API}/nemt/admin/rides/${path}`, {
    headers: { "X-Dispatcher-Auth": auth },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Server ${res.status}`);
  return data;
}

export function PaymentActionModal({ ride, auth, onClose, onSaved }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [finalFare, setFinalFare] = useState("");
  const [amount, setAmount] = useState("");
  const [offlineMethod, setOfflineMethod] = useState("cash");
  const [offlineNote, setOfflineNote] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await apiGet(auth, `${ride.id}/payment-info`);
        if (!mounted) return;
        setInfo(data);
        const balance = data.balance_owed_cents || 0;
        const captured = data.captured_amount_cents || 0;
        const finalC = data.final_fare_cents || 0;
        const suggested = data.suggested_final_fare_cents || 0;
        // Prefill priority: locked-in final fare > calculator's suggested fare > captured.
        // This means dispatch NEVER has to rebuild the trip — the amount to
        // charge is already sitting in the box, ready to send.
        const prefillFareCents = finalC || suggested || captured;
        setFinalFare(prefillFareCents ? (prefillFareCents / 100).toFixed(2) : "");
        setAmount(balance ? (balance / 100).toFixed(2) : "");
      } catch (err) {
        toast.error(err.message || "Failed to load payment info");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [auth, ride.id]);

  const dollarsToCents = (v) => Math.round(parseFloat(v || "0") * 100);

  const saveFare = async () => {
    setBusy("fare");
    try {
      const res = await apiPost(auth, `${ride.id}/adjust-fare`, {
        final_fare_cents: dollarsToCents(finalFare),
      });
      toast.success(res.balance_owed_cents ? `Balance owed: ${money(res.balance_owed_cents)}` : "Fare set — no balance owed");
      const fresh = await apiGet(auth, `${ride.id}/payment-info`);
      setInfo(fresh);
      setAmount(fresh.balance_owed_cents ? (fresh.balance_owed_cents / 100).toFixed(2) : "");
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy("");
    }
  };

  const chargeCard = async () => {
    const cents = dollarsToCents(amount);
    if (cents < 100) return toast.error("Enter an amount of at least $1.00");
    setBusy("charge");
    try {
      const res = await apiPost(auth, `${ride.id}/charge-balance`, { amount_cents: cents });
      toast.success(`Charged ${money(res.amount_cents)} to card on file`);
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy("");
    }
  };

  const chargeFullAndReleaseHold = async () => {
    const cents = dollarsToCents(finalFare);
    if (cents < 100) return toast.error("Set a final fare of at least $1.00 first");
    const confirm = window.confirm(
      `Charge ${info.member_name} the FULL ${money(cents)} to the card on file AND release the existing hold?\n\n` +
      `This will:\n` +
      `  • Cancel the outstanding $${((info.hold_amount_cents || 0) / 100).toFixed(2)} authorization (funds return to their bank)\n` +
      `  • Charge ${money(cents)} off-session (no customer action needed)\n` +
      `  • Mark the ride completed`
    );
    if (!confirm) return;
    setBusy("chargefull");
    try {
      const res = await apiPost(auth, `${ride.id}/charge-final-and-release-hold`, { amount_cents: cents });
      toast.success(
        res.released_hold_pi
          ? `Charged ${money(res.amount_cents)} · Hold released`
          : `Charged ${money(res.amount_cents)}`
      );
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy("");
    }
  };

  const releaseHoldAndConfirm = async () => {
    const cents = dollarsToCents(finalFare || amount);
    if (cents < 100) return toast.error("Set the amount collected (at least $1.00)");
    const method = window.prompt("Payment method received?\n\ncash / zelle / check / card_offline / other", "cash");
    if (!method) return;
    const note = window.prompt("Optional note (bank ref, receipt #, etc.):", "") || "";
    const confirmed = window.confirm(
      `Release the Stripe hold AND confirm ${money(cents)} received via ${method.toUpperCase()}?\n\n` +
      `This will:\n` +
      `  • Cancel the outstanding Stripe authorization (funds return to their bank)\n` +
      `  • Record the ${method} payment on this ride\n` +
      `  • Mark the ride Completed if fully paid`
    );
    if (!confirmed) return;
    setBusy("release-confirm");
    try {
      const res = await apiPost(auth, `${ride.id}/release-hold-and-confirm-payment`, {
        amount_cents: cents,
        method: method.trim().toLowerCase(),
        note: note.trim() || undefined,
      });
      const releaseNote = res.hold_release === "released" ? " · Hold released"
        : res.hold_release === "already_captured" ? " · ⚠️ hold was already captured — refund manually if needed"
        : "";
      toast.success(`Recorded ${money(res.offline_amount_cents)} ${method.toUpperCase()}${releaseNote}`);
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message || "Release + confirm failed");
    } finally {
      setBusy("");
    }
  };

  const sendLink = async () => {
    const cents = dollarsToCents(amount);
    if (cents < 100) return toast.error("Enter an amount of at least $1.00");
    setBusy("link");
    try {
      const res = await apiPost(auth, `${ride.id}/send-payment-link`, {
        amount_cents: cents,
        origin_url: window.location.origin,
      });
      toast.success(res.email_sent
        ? `Payment link emailed to ${info.member_email}`
        : "Link created — email delivery failed, copy manually");
      if (!res.email_sent && res.checkout_url) {
        try { await navigator.clipboard.writeText(res.checkout_url); toast.info("Link copied to clipboard"); } catch { /* ignore */ }
      }
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy("");
    }
  };

  const reconcileStatus = async () => {
    setBusy("reconcile");
    try {
      const res = await apiPost(auth, `${ride.id}/reconcile-status`, {});
      if (res.flipped_to_completed) {
        toast.success("Ride status corrected → Completed");
      } else if (res.balance_owed_cents > 0) {
        toast.info(`Still owed: ${money(res.balance_owed_cents)} — status unchanged`);
      } else {
        toast.info(`Reconciled — status stays "${res.ride_status}" (trip performed: ${res.trip_performed ? "yes" : "no"})`);
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy("");
    }
  };

  const collectOffline = async () => {
    const cents = dollarsToCents(amount);
    if (cents < 0) return toast.error("Amount can't be negative");
    setBusy("offline");
    try {
      const res = await apiPost(auth, `${ride.id}/mark-collected-offline`, {
        amount_cents: cents,
        method: offlineMethod,
        note: offlineNote,
      });
      toast.success(`Recorded ${money(res.amount_cents)} via ${res.method}`);
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy("");
    }
  };

  const reasonInfo = REASON_COPY[ride.action_reason] || REASON_COPY.manual_review;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="payment-action-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-brand-navy/10 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">Payment action</div>
            <h2 className="font-serif text-2xl text-brand-deep leading-tight">{ride.member_name}</h2>
          </div>
          <button onClick={onClose} data-testid="payment-modal-close" className="p-2 hover:bg-slate-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center text-brand-muted"><Loader2 className="inline w-6 h-6 animate-spin" /></div>
        ) : !info ? null : (
          <div className="p-6 space-y-5">
            {/* Reason chip */}
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs uppercase font-semibold tracking-wider ${reasonInfo.tone}`}>
              <AlertCircle className="w-3.5 h-3.5" />
              {reasonInfo.label}
            </div>
            <p className="text-sm text-brand-muted leading-relaxed">{reasonInfo.hint}</p>

            {/* Contact + card */}
            <div className="bg-brand-surfaceAlt rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-semibold">Patient</div>
                <div className="text-brand-deep font-medium mt-1">{info.member_name}</div>
                <div className="text-xs text-brand-muted">{info.member_phone || "no phone"}</div>
                <div className="text-xs text-brand-muted">{info.member_email || "no email"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-semibold">Card on file</div>
                {info.has_saved_card ? (
                  <div className="mt-1 flex items-center gap-2 text-brand-deep font-medium">
                    <CreditCard className="w-4 h-4 text-brand-green" />
                    {info.card_brand ? info.card_brand.toUpperCase() : "Card"} ····{info.card_last4 || "····"}
                  </div>
                ) : (
                  <div className="mt-1 text-xs text-red-700 font-medium">No saved card — hold was never placed</div>
                )}
              </div>
            </div>

            {/* Totals */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="bg-white border border-brand-navy/10 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-semibold">Hold</div>
                <div className="font-serif text-lg text-brand-deep">{money(info.hold_amount_cents)}</div>
              </div>
              <div className="bg-white border border-brand-navy/10 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-semibold">Captured</div>
                <div className="font-serif text-lg text-emerald-700">{money(info.captured_amount_cents)}</div>
              </div>
              <div className="bg-white border border-brand-navy/10 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-semibold">Final fare</div>
                <div className="font-serif text-lg text-brand-deep">{money(info.final_fare_cents)}</div>
              </div>
              <div className="bg-white border border-amber-300 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-amber-800 font-semibold">Balance</div>
                <div className="font-serif text-lg text-amber-800">{money(info.balance_owed_cents)}</div>
              </div>
            </div>

            {/* Adjust final fare */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-blue-800 font-bold mb-2">Adjust final fare</div>
              <p className="text-xs text-blue-900 mb-1">Set the actual total for this trip. Anything above what was captured becomes the balance owed.</p>
              {info.suggested_final_fare_cents > 0 && (
                <p className="text-xs text-emerald-800 mb-3" data-testid="suggested-fare-line">
                  <strong>Calculator says: {money(info.suggested_final_fare_cents)}</strong>
                  {info.total_miles ? <> · {info.total_miles} mi</> : null}
                  {(info.stops && info.stops.length) ? <> · {info.stops.length} stop{info.stops.length === 1 ? "" : "s"}</> : null}
                  {" "}(pre-filled below — edit if driver added extra miles or waited longer)
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[140px]">
                  <span className="absolute left-3 top-2.5 text-brand-muted">$</span>
                  <input
                    type="number" step="0.01" min="0"
                    value={finalFare}
                    onChange={(e) => setFinalFare(e.target.value)}
                    data-testid="final-fare-input"
                    className="w-full pl-7 pr-3 py-2 border border-blue-300 rounded-lg text-brand-deep focus:outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/15"
                  />
                </div>
                <button onClick={saveFare} disabled={busy === "fare"} data-testid="save-final-fare"
                  className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white text-sm font-medium px-4 py-2 rounded-full transition disabled:opacity-60">
                  {busy === "fare" ? <Loader2 className="w-4 h-4 animate-spin" /> : <DollarSign className="w-4 h-4" />} Save fare
                </button>
              </div>
            </div>

            {/* Amount for payment actions */}
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-brand-muted font-semibold mb-2">Amount to collect</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-brand-muted">$</span>
                <input
                  type="number" step="0.01" min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="collect-amount-input"
                  className="w-full pl-7 pr-3 py-2 border border-brand-navy/15 rounded-lg text-brand-deep focus:outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/15"
                />
              </div>
            </div>

            {/* Primary action for guest / unlinked bookings — one-tap void hold + full charge */}
            {(info.has_saved_card || info.can_recover_card) && (
              <button
                onClick={chargeFullAndReleaseHold}
                disabled={busy === "chargefull"}
                data-testid="charge-full-release-hold"
                className="w-full inline-flex items-center justify-center gap-2 bg-brand-green hover:bg-brand-greenDark text-white text-sm font-semibold py-3 rounded-full transition disabled:opacity-40"
                title="Cancels the existing hold in Stripe (returns funds to bank) and charges the full final fare off-session to the same card. Works even for guest bookings — recovers the card from the checkout session."
              >
                {busy === "chargefull" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                Charge full ${finalFare || "0.00"} & release ${((info.hold_amount_cents || 0) / 100).toFixed(2)} hold
              </button>
            )}

            {/* Action buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={chargeCard}
                disabled={!info.has_saved_card || busy === "charge"}
                data-testid="charge-saved-card"
                className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold py-3 rounded-full transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy === "charge" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                Charge saved card
              </button>
              <button
                onClick={sendLink}
                disabled={!info.member_email || busy === "link"}
                data-testid="send-payment-link"
                className="inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white text-sm font-semibold py-3 rounded-full transition disabled:opacity-40"
              >
                {busy === "link" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Send payment link
              </button>
            </div>

            {/* Offline */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-amber-800 font-bold mb-3">Record offline payment</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <select
                  value={offlineMethod}
                  onChange={(e) => setOfflineMethod(e.target.value)}
                  data-testid="offline-method"
                  className="px-3 py-2 border border-amber-300 rounded-lg text-brand-deep bg-white focus:outline-none focus:border-brand-green"
                >
                  <option value="cash">Cash</option>
                  <option value="zelle">Zelle</option>
                  <option value="check">Check</option>
                  <option value="other">Other</option>
                </select>
                <input
                  type="text"
                  placeholder="Note (optional)"
                  value={offlineNote}
                  onChange={(e) => setOfflineNote(e.target.value)}
                  data-testid="offline-note"
                  className="px-3 py-2 border border-amber-300 rounded-lg text-brand-deep bg-white focus:outline-none focus:border-brand-green"
                />
              </div>
              <button
                onClick={collectOffline}
                disabled={busy === "offline"}
                data-testid="mark-collected-offline"
                className="w-full inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold py-3 rounded-full transition disabled:opacity-60"
              >
                {busy === "offline" ? <Loader2 className="w-4 h-4 animate-spin" /> : <HandCoins className="w-4 h-4" />}
                Mark collected offline
              </button>
            </div>

            {info.action_error && (
              <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg p-3">
                <strong>Last error:</strong> {info.action_error}
              </div>
            )}

            {/* Release hold + confirm offline payment — for when the customer
                paid outside Stripe (cash / Zelle / check). Cancels the auth
                and records the payment in one tap. */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center justify-between gap-3">
              <div className="text-xs text-amber-900 flex-1 min-w-0">
                <div className="font-semibold text-amber-900">Customer paid outside Stripe?</div>
                <div>Release the outstanding hold and record the cash / Zelle / check received.</div>
              </div>
              <button
                onClick={releaseHoldAndConfirm}
                disabled={busy === "release-confirm"}
                data-testid="release-hold-and-confirm"
                className="shrink-0 inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white text-xs font-semibold px-4 py-2 rounded-full disabled:opacity-40"
              >
                {busy === "release-confirm" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <HandCoins className="w-3.5 h-3.5" />}
                Release hold & confirm payment
              </button>
            </div>

            {/* Reconcile: fix a stuck 'pending payment' status when the money is
                already in (Stripe captured OR offline recorded). Safe no-op if
                the ride isn't actually paid. */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center justify-between gap-3">
              <div className="text-xs text-slate-600 flex-1 min-w-0">
                <div className="font-semibold text-slate-800">Ride paid but still showing "pending payment"?</div>
                <div>Re-check the totals against the recorded payments — flips status to <b>Completed</b> if fully paid.</div>
              </div>
              <button
                onClick={reconcileStatus}
                disabled={busy === "reconcile"}
                data-testid="reconcile-status"
                className="shrink-0 inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold px-4 py-2 rounded-full disabled:opacity-40"
              >
                {busy === "reconcile" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                Reconcile status
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
